import { useMediaQuery } from '@mantine/hooks';
import { Button, Group, Stack, Switch } from '@mantine/core';
import { Workflow } from '@/app/workflows/actions/interfaces';
import { useCallback, useState } from 'react';
import { getActiveWorkflowTab, WorkflowTab } from '@/app/workflows/utils/navigation';
import WorkflowList from '@/app/workflows/components/v6/molecues/WorkflowList';
import { WorkflowTabsControlled } from '@/app/workflows/components/v6/atoms/WorkflowTabsControlled';
import { useScrollToTop, useWorkflows } from '@/app/workflows/components/v6/organism/run/hooks';
import { WorkflowSearchControlled } from '@/app/workflows/components/v6/molecues/WorkflowSearchControlled';
import { ScrollableModal } from '@/app/components/modal/ScrollableModal';
import { showErrorNotification, showWarningNotification } from '@/app/components/shared/notifications';
import { usePendingOutputsConsumer } from '@/app/assets/components/table/hooks/usePendingOutputs';
import { useRowSelection } from '@/app/assets/contexts/RowSelectionContext';
import { useRunWorkflow } from '@/app/assets/contexts/RunWorkflowContext';
import { ListSkeleton } from '@/app/assets/components/table/Skeletons';
import { showCantRunWorkflowNotification } from '@/app/assets/components/table/hooks/useAssetGroupActions';
import { OnboardingFlows } from '@/app/contexts/OnboardingContext/constants';
import { useOnboarding } from '@/app/contexts/OnboardingContext/OnboardingContext';
import { PreviewNewSettingsToggle } from './PreviewNewSettingsToggle';
import { getImpersonation } from '@/utils/impersonation';
import { WorkflowsErrorAlert } from '../../../table/WorkflowsErrorAlert';

interface RunWorkflowProps {
  onRun: (workflow: Workflow) => void;
  canBeOpened: boolean;
  isUnprocessableSelected: boolean;
}

export function RunWorkflow({ onRun, canBeOpened, isUnprocessableSelected }: RunWorkflowProps) {
  const { workflows, searchUserInput, setWorkflowTab, workflowTab, setSearchUserInput, setSearchTerm, error } =
    useWorkflows();
  const { opened, open, close } = useRunWorkflow();
  const isLargeViewPort = useMediaQuery('(min-width: 1800px)');
  const { addOptimisticOutput } = usePendingOutputsConsumer();
  const { getSelectedRawAssetsIds } = useRowSelection();
  const { runFlow } = useOnboarding();

  const onRunInternal = useCallback(
    (workflow: Workflow) => {
      getSelectedRawAssetsIds().forEach((rawModelId) => {
        addOptimisticOutput({
          name: workflow.name,
          optimisationStatus: 'sent_to_queue',
          outputId: workflow.id,
          progress: 0,
          rawModelId,
          startedAt: new Date().toISOString(),
          workflowId: workflow.id,
          workflowName: workflow.name,
        });
      });
      onRun(workflow);
    },
    [onRun, addOptimisticOutput, getSelectedRawAssetsIds]
  );

  const { viewport, scrollToTop } = useScrollToTop();
  const onWorkflowAdded = useCallback(() => {
    if (workflowTab !== WorkflowTab.ALL || WorkflowTab.CUSTOM) {
      setWorkflowTab(WorkflowTab.CUSTOM);
    }
    scrollToTop();
  }, [scrollToTop, setWorkflowTab, workflowTab]);

  const onOpenModalClick = () => {
    const { isImpersonating } = getImpersonation();
    if (isImpersonating) {
      return showErrorNotification({
        title: 'Not allowed',
        message: 'You cannot run presets while impersonating another user.',
      });
    }
    if (!canBeOpened) {
      return showCantRunWorkflowNotification();
    }

    if (isUnprocessableSelected) {
      showWarningNotification({
        title: 'Unprocessable model(s) selected',
        message:
          'You cannot use output models, failed imports or pending uploads for this operation, such models will be ignored and not processed further.',
      });
    }

    open();
  };

  return (
    <>
      <ScrollableModal
        id='run-workflow-modal'
        opened={opened}
        size={isLargeViewPort ? '75%' : '100%'}
        onClose={close}
        title='Select a 3D Processor Preset'
        viewportRef={viewport}
        onTransitionEnd={() => {
          if (opened) {
            runFlow(OnboardingFlows.PRESET_MODAL);
          }
        }}
        nonScrollableTop={
          <Stack p='xs' mt='md' mb='sm'>
            <WorkflowSearchControlled
              searchTerm={searchUserInput}
              submitSearch={() => setSearchTerm(searchUserInput)}
              setSearchTerm={setSearchUserInput}
              clearSearch={() => {
                setSearchTerm('');
                setSearchUserInput('');
              }}
            />
            <Group align='center' justify='space-between'>
              <WorkflowTabsControlled
                onChange={(value) => setWorkflowTab(getActiveWorkflowTab(value))}
                value={workflowTab}
              />
              <PreviewNewSettingsToggle />
            </Group>
          </Stack>
        }
        nonScrollableBottom={
          <Group justify='flex-end' h={60}>
            <Button variant='default' onClick={close}>
              Close
            </Button>
          </Group>
        }
      >
        {error && <WorkflowsErrorAlert error={error.message} />}
        {workflows ? (
          <WorkflowList workflows={workflows} onRun={onRunInternal} onWorkflowAdded={onWorkflowAdded} />
        ) : (
          <ListSkeleton />
        )}
      </ScrollableModal>
      <Button onClick={onOpenModalClick} id={`${OnboardingFlows.RUN_PRESET}-1`}>
        Run 3D Processor
      </Button>
    </>
  );
}
